import React from "react"
import { useTranslation } from "react-i18next"

import { relativeTime } from "../helpers/relative-time.ts"
import { EntityIcon } from "../map/EntityIcon.tsx"
import type { AnalyticsRecentActivityEvent } from "../redux/global-map.types"

interface RecentActivityCardProps {
  events: AnalyticsRecentActivityEvent[]
  onSelectEvent: (event: AnalyticsRecentActivityEvent) => void
}

/**
 * Spec §11: five most recent permitted events, newest first (already
 * deduplicated server-side — `analytics.py::_recent_activity`). Selecting
 * an event opens the related record — the same `selectFeature`-shaped
 * navigation every other Global Map row click already uses.
 */
export function RecentActivityCard({
  events,
  onSelectEvent
}: RecentActivityCardProps) {
  // The "Recent Activity" heading and its "View All" control are rendered by
  // the enclosing `AnalyticsCard`'s own header (title + `headerExtra`) — this
  // used to repeat both inside the card body, so the section read with two
  // stacked headings. Only the feed itself lives here now.
  return (
    <ul className="m-0 flex list-none flex-col p-0">
      {events.map((event, index) => (
        <li
          key={`${event.recordId}-${event.timestamp}-${index}`}
          className="border-t border-slate-100 first:border-t-0"
        >
          <button
            type="button"
            onClick={() => onSelectEvent(event)}
            className="flex w-full min-w-0 items-start gap-2.5 rounded-md px-1.5 py-2 text-left transition-colors hover:bg-slate-50"
          >
            <EntityIcon
              entityType={event.entityType}
              className="mt-0.5 size-6 shrink-0"
            />
            <div className="min-w-0 flex-1">
              <div className="truncate text-[13px] font-medium text-slate-900">
                {event.description}
              </div>
              <div className="truncate text-xs text-slate-500">
                {event.recordTitle}
              </div>
            </div>
            <span className="mt-0.5 shrink-0 whitespace-nowrap text-[11px] text-slate-400">
              {relativeTime(event.timestamp)}
            </span>
          </button>
        </li>
      ))}
    </ul>
  )
}

/**
 * The card header's "View All" — rendered into `AnalyticsCard`'s own
 * `headerExtra` slot by `AnalyticsTab`, so the heading row carries it exactly
 * once. Same `onViewAll` handler and behavior as before.
 */
export function RecentActivityViewAllButton({
  onViewAll
}: {
  onViewAll: () => void
}) {
  const { t } = useTranslation()
  return (
    <button
      type="button"
      onClick={onViewAll}
      className="cursor-pointer border-none bg-transparent p-0 text-xs font-semibold text-primary hover:underline"
    >
      {t("globalMap.analytics.recentActivity.viewAll")}
    </button>
  )
}
